/* eslint-disable no-unused-vars */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTheme } from '../../context/ThemeContext';
import { useAdminAuth } from '../../context/AdminAuthContext';
import axiosClient from '../../api/axiosClient';
import { widgetStyles, animations } from '../../utils/designTokens';
import { formatAiProviderLabel, getAiProviderBadgeClass } from '../../utils/aiProvider';
import toast from 'react-hot-toast';
import SimpleRevealSlider from '../../components/SimpleRevealSlider';
import { AdminAIComparisonModal, AdminAIThumbnailGallery } from '../../components/admin/AdminAIImageComponents';

// Brand accent color
const ACCENT = '#7d6633';

// Normalize any stored image path (including bad file:/// URLs) to a usable http(s) URL.
const getBackendOrigin = () => {
  try {
    const base = axiosClient?.defaults?.baseURL || '';
    if (base) return new URL(base, window.location.origin).origin; // base may be "/api" or "https://.../api"
  } catch (_) {}
  return window.location.origin;
};

const toPublicImageUrl = (input) => {
  if (!input || typeof input !== 'string') return '';
  if (input.startsWith('http') || input.startsWith('data:')) return input;

  let s = input;

  // Handle file:/// URIs (Chrome blocks these in <img>)
  if (s.startsWith('file:///')) {
    try { s = decodeURIComponent(s.slice('file:///'.length)); } catch (_) { s = s.slice('file:///'.length); }
  }

  // Normalize Windows paths and mixed slashes
  const normalized = s.replace(/\\/g, '/');
  const lower = normalized.toLowerCase();

  // Extract "/uploads/..." from any absolute path that contains it
  const idx = lower.indexOf('/uploads/');
  if (idx >= 0) {
    const rel = normalized.slice(idx); // "/uploads/..."
    return `${getBackendOrigin()}${rel}`;
  }

  // Relative uploads path
  if (lower.startsWith('/uploads/')) return `${getBackendOrigin()}${normalized}`;
  if (lower.startsWith('uploads/')) return `${getBackendOrigin()}/${normalized}`;

  // Unknown format: return as-is
  return input;
};

// Glass morphism Card component
const Card = ({ children, className = '' }) => (
  <div
    className={
      'rounded-2xl shadow-lg border border-white/20 dark:border-white/10 bg-white/70 dark:bg-zinc-900/60 backdrop-blur-xl transition-all duration-300 ' +
      className
    }
  >
    {children}
  </div>
);

// Card Header component
const CardHeader = ({ title, icon, action, subtitle }) => (
  <div className="flex items-center justify-between p-4 md:p-5 border-b border-white/10 dark:border-white/10">
    <div className="flex items-center gap-3">
      <div className="h-10 w-10 rounded-xl bg-[--accent]/10 flex items-center justify-center" style={{ '--accent': ACCENT }}>
        {icon}
      </div>
      <div>
        <h3 className="font-semibold text-zinc-900 dark:text-zinc-100">{title}</h3>
        {subtitle && (
          <p className="text-xs text-zinc-500 dark:text-zinc-400 mt-0.5">{subtitle}</p>
        )}
      </div>
    </div>
    {action}
  </div>
);

// Provider Badge Component
const ProviderBadge = ({ provider }) => {
  return (
    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getAiProviderBadgeClass(provider)}`}>
      {formatAiProviderLabel(provider).toUpperCase()}
    </span>
  );
};

// Status Badge Component
const StatusBadge = ({ status }) => {
  const colors = {
    completed: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
    pending: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300',
    processing: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300',
    failed: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300'
  };

  return (
    <span className={`px-2 py-1 rounded-full text-xs font-medium ${colors[status] || 'bg-gray-100 text-gray-800'}`}>
      {status?.toUpperCase() || 'UNKNOWN'}
    </span>
  );
};

// Image Gallery Component
const ImageGallery = ({ images, originalImage, scanId, clientName, onImageClick }) => {
  const [selectedImage, setSelectedImage] = useState(null);
  const [showModal, setShowModal] = useState(false);


  const closeModal = () => {
    setShowModal(false);
    setSelectedImage(null);
  };

  const downloadImage = async (imagePath, filename) => {
    try {
      const res = await fetch(toPublicImageUrl(imagePath));
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Image downloaded successfully');
    } catch (error) {
      console.error('Download failed:', error);
      toast.error('Failed to download image');
    }
  };

  return (
    <>
      <div className="space-y-3">
        {/* Original Image */}
        {originalImage && (
          <div>
            <h4 className="text-xs font-medium text-zinc-700 dark:text-zinc-300 mb-1">Original Photo</h4>
            <div className="relative group cursor-pointer" onClick={() => onImageClick({ path: originalImage }, 'original')}>
              <img
                src={toPublicImageUrl(originalImage)}
                alt="Original"
                className="w-full h-20 object-cover rounded-lg transition-transform group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-all rounded-lg flex items-center justify-center">
                <div className="opacity-0 group-hover:opacity-100 transition-opacity">
                  <svg className="h-6 w-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Generated Images */}
        {images && images.length > 0 && (
          <div>
            <h4 className="text-xs font-medium text-zinc-700 dark:text-zinc-300 mb-1">
              AI Generated ({images.length})
            </h4>
            <div className="grid grid-cols-3 gap-1">
              {images.slice(0, 6).map((image, index) => (
                <div
                  key={index} 
                  className="relative group cursor-pointer"
                  onClick={() => onImageClick(image, 'generated')}
                >
                  <img
                    src={toPublicImageUrl(image.path)}
                    alt={`Generated ${index + 1}`}
                    className="w-full h-16 object-cover rounded transition-transform group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-40 transition-all rounded flex items-center justify-center">
                    <div className="opacity-0 group-hover:opacity-100 transition-opacity">
                      <svg className="h-4 w-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                      </svg>
                    </div>
                  </div>
                </div>
              ))}
              {images.length > 6 && (
                <div className="h-16 rounded border border-white/20 dark:border-white/10 bg-zinc-100 dark:bg-zinc-700 flex items-center justify-center">
                  <span className="text-xs text-zinc-600 dark:text-zinc-400">
                    +{images.length - 6}
                  </span>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Image Modal */}
      <AnimatePresence>
        {showModal && selectedImage && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4 overflow-y-auto"
            onClick={closeModal}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="relative max-w-4xl max-h-[85vh] w-full my-8"
              onClick={(e) => e.stopPropagation()}
            >
              <Card className="overflow-hidden">
                {/* Modal Header */}
                <div className="flex items-center justify-between p-4 border-b border-white/10">
                  <div>
                    <h3 className="font-semibold text-zinc-900 dark:text-zinc-100">
                      {selectedImage.type === 'original' ? 'Original Photo' : 'AI Generated Hairstyle'}
                    </h3>
                    <p className="text-sm text-zinc-600 dark:text-zinc-400">
                      Client: {clientName}
                    </p>
                  </div>
                  <button
                    onClick={closeModal}
                    className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-full transition-colors"
                  >
                    <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>

                {/* Modal Body */}
                <div className="p-4">
                  {(() => {
                    const before = originalImage || selectedImage.path;
                    const firstGen = (images && images.length > 0 && images[0]?.path) ? images[0].path : before;
                    const after = selectedImage.type === 'original' ? firstGen : selectedImage.path;
                    return (
                      <SimpleRevealSlider
                        beforeSrc={toPublicImageUrl(before)}
                        afterSrc={toPublicImageUrl(after)}
                        altBefore="Original"
                        altAfter="Generated"
                      />
                    );
                  })()}

                  {/* Metadata */}
                  <div className="mt-4 grid grid-cols-1 lg:grid-cols-2 gap-4">
                    {/* Analysis Information */}
                    <div className="p-4 bg-white/50 dark:bg-zinc-800/50 rounded-lg">
                      <h4 className="font-medium text-zinc-900 dark:text-zinc-100 mb-3">Analysis Information</h4>
                      <div className="space-y-2 text-sm">
                        {selectedImage.faceShape && (
                          <div className="flex justify-between">
                            <span className="text-zinc-600 dark:text-zinc-400">Face Shape:</span>
                            <span className="font-medium">{selectedImage.faceShape}</span>
                          </div>
                        )}
                        {selectedImage.haircut && (
                          <div className="flex justify-between">
                            <span className="text-zinc-600 dark:text-zinc-400">Haircut:</span>
                            <span className="font-medium">{selectedImage.haircut}</span>
                          </div>
                        )}
                        {selectedImage.hair_color && (
                          <div className="flex justify-between">
                            <span className="text-zinc-600 dark:text-zinc-400">Hair Color:</span>
                            <span className="font-medium">{selectedImage.hair_color}</span>
                          </div>
                        )}
                        {selectedImage.provider && (
                          <div className="flex justify-between items-center">
                            <span className="text-zinc-600 dark:text-zinc-400">AI Provider:</span>
                            <span className="font-medium">
                              {formatAiProviderLabel(selectedImage.provider)}
                            </span>
                          </div>
                        )}
                        {selectedImage.generatedAt && (
                          <div className="flex justify-between">
                            <span className="text-zinc-600 dark:text-zinc-400">Generated:</span>
                            <span className="font-medium text-xs">
                              {new Date(selectedImage.generatedAt).toLocaleDateString()}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="space-y-3">
                      <motion.button
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        onClick={() => downloadImage(selectedImage.path, `${clientName}-${selectedImage.type}.jpg`)}
                        className="w-full px-4 py-2 bg-emerald-600 text-white rounded-lg text-sm font-medium hover:bg-emerald-700 transition-colors flex items-center justify-center gap-2"
                      >
                        <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                        Download Image
                      </motion.button>
                    </div>
                  </div>
                </div>
              </Card>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

const EnhancedAIHairstyleAnalysis = () => {
  const { isDark } = useTheme();
  const { hasPermission } = useAdminAuth();
  const REMOVED_SCANS_SESSION_KEY = 'ai_analysis_removed_scans_count';

  // State management
  const [scans, setScans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [analytics, setAnalytics] = useState(null);
  const [ledgerCreditsUsed, setLedgerCreditsUsed] = useState(0); // From credit_ledger (immutable, never changes with deletes)

  // Selection + confirmation modal (pattern aligned with Clients page)
  const [selectedScans, setSelectedScans] = useState(new Set());
  const [bulkActionLoading, setBulkActionLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState({});
  // Successful deletes in this browser session (persists across refresh, resets when tab/session ends)
  const [removedScansCount, setRemovedScansCount] = useState(() => {
    try {
      const raw = sessionStorage.getItem(REMOVED_SCANS_SESSION_KEY);
      const n = parseInt(raw || '0', 10);
      return Number.isFinite(n) && n >= 0 ? n : 0;
    } catch (_) {
      return 0;
    }
  });
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmData, setConfirmData] = useState(null);
  const selectAllRef = useRef(null);
  
  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [activeScan, setActiveScan] = useState(null);

  const openImageModal = (image, type, scan) => {
    setSelectedImage({ ...image, type });
    setActiveScan(scan || null);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedImage(null);
  };

  const downloadImage = async (imagePath, filename) => {
    try {
      const response = await axiosClient.get(imagePath, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Image downloaded successfully');
    } catch (error) {
      console.error('Download failed:', error);
      toast.error('Failed to download image');
    }
  };

  // Filters
  const [filters, setFilters] = useState({
    clientSearch: '',
    dateFrom: '',
    dateTo: '',
    limit: 20,
    offset: 0
  });

  // Load data
  const loadScans = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      
      Object.entries(filters).forEach(([key, value]) => {
        if (value) params.append(key, value);
      });

      const response = await axiosClient.get(`/hairstyles/admin/all?${params}`);
      
      if (response.data.success) {
        // Normalize any stored image paths to avoid file:/// rendering issues
        const normalizedScans = (response.data.scans || []).map((s) => ({
          ...s,
          originalImage: toPublicImageUrl(s.originalImage),
          resultUrl: toPublicImageUrl(s.resultUrl),
          legacyStyledImages: Array.isArray(s.legacyStyledImages) ? s.legacyStyledImages.map(toPublicImageUrl) : [],
          generatedImages: Array.isArray(s.generatedImages)
            ? s.generatedImages.map((img) => ({ ...img, path: toPublicImageUrl(img.path || img.url), url: toPublicImageUrl(img.url || img.path) }))
            : []
        }));
        setScans(normalizedScans);
      } else {
        throw new Error(response.data.message || 'Failed to load scans');
      }
    } catch (error) {
      const status = error?.response?.status;
      const serverMsg =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.response?.data?.details ||
        error?.message;

      console.error('Error loading scans:', { status, serverMsg, error });
      toast.error(serverMsg ? `Failed to load scans${status ? ` (${status})` : ''}: ${serverMsg}` : 'Failed to load hairstyle analysis data');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  const loadCreditsFromLedger = useCallback(async () => {
    try {
      // Fetch credits from ledger (source of truth)
      const creditsRes = await axiosClient.get('/credits/me');
      if (creditsRes.data.ok) {
        // Credits Used = total granted - remaining (immutable, never changes with scan deletes)
        const creditsUsed = creditsRes.data.credits_used || 0;
        setLedgerCreditsUsed(creditsUsed);
      }
    } catch (error) {
      console.error('Error loading credits from ledger:', error);
    }
  }, []);

  const loadAnalytics = useCallback(async () => {
    try {
      const analyticsRes = await axiosClient.get('/hairstyles/admin/analytics');

      if (analyticsRes.data.success) {
        setAnalytics(analyticsRes.data.analytics);
      }
    } catch (error) {
      console.error('Error loading analytics:', error);
    }
  }, []);

  useEffect(() => {
    if (hasPermission('ai-analysis')) {
      loadScans();
      loadCreditsFromLedger();
      loadAnalytics();
    }
  }, [hasPermission, loadScans, loadCreditsFromLedger, loadAnalytics]);

  // Persist removedScansCount across refresh in the current tab/session.
  useEffect(() => {
    try {
      sessionStorage.setItem(REMOVED_SCANS_SESSION_KEY, String(removedScansCount || 0));
    } catch (_) {}
  }, [removedScansCount]);

  // Clear selection when filters change (matches behavior in Clients page)
  useEffect(() => {
    setSelectedScans(new Set());
  }, [filters]);

  // Keep Select All checkbox indeterminate state in sync
  useEffect(() => {
    if (!selectAllRef.current) return;
    const total = scans.length;
    const selected = selectedScans.size;
    selectAllRef.current.indeterminate = selected > 0 && selected < total;
  }, [scans.length, selectedScans]);

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value,
      offset: 0 // Reset to first page when filtering
    }));
  };

  // Select all / selection helpers
  const handleSelectAll = () => {
    if (scans.length === 0) return;
    if (selectedScans.size === scans.length) {
      setSelectedScans(new Set());
    } else {
      setSelectedScans(new Set(scans.map(s => s.id)));
    }
  };

  const handleSelectScan = (scanId) => {
    setSelectedScans(prev => {
      const next = new Set(prev);
      if (next.has(scanId)) next.delete(scanId);
      else next.add(scanId);
      return next;
    });
  };

  const performDeleteScan = async (scanId) => {
    setActionLoading(prev => ({ ...prev, [scanId]: true }));
    try {
      await axiosClient.delete(`/hairstyles/admin/scan/${scanId}`);
      toast.success('Scan deleted successfully');
      setScans(prev => prev.filter(s => s.id !== scanId));
      setRemovedScansCount(prev => prev + 1);
      setSelectedScans(prev => {
        const next = new Set(prev);
        next.delete(scanId);
        return next;
      });
    } catch (error) {
      console.error('Error deleting scan:', error);
      toast.error('Failed to delete scan');
    } finally {
      setActionLoading(prev => ({ ...prev, [scanId]: false }));
    }
  };

  const handleDeleteScan = async (scanId) => {
    const scan = scans.find(s => s.id === scanId);
    setConfirmData({
      type: 'delete',
      title: 'Delete Scan',
      message: 'Are you sure you want to permanently delete this scan and all associated images?',
      details: [
        `Scan #${scanId}`,
        `Client: ${scan?.client?.name || scan?.client_name || 'Unknown'}`
      ],
      warning: 'This action cannot be undone!',
      onConfirm: () => performDeleteScan(scanId)
    });
    setShowConfirmModal(true);
  };

  const handleBulkDelete = async () => {
    if (selectedScans.size === 0) return;

    const selectedScanData = scans.filter(s => selectedScans.has(s.id));
    setConfirmData({
      type: 'bulk_delete',
      title: 'Delete Multiple Scans',
      message: `Are you sure you want to permanently delete ${selectedScans.size} scan(s)?`,
      details: selectedScanData.slice(0, 5).map(s =>
        `• Scan #${s.id} — ${s?.client?.name || s?.client_name || 'Unknown'}`
      ).concat(selectedScanData.length > 5 ? [`... and ${selectedScanData.length - 5} more`] : []),
      warning: 'This action cannot be undone!',
      onConfirm: () => performBulkDelete()
    });
    setShowConfirmModal(true);
  };

  const performBulkDelete = async () => {
    setBulkActionLoading(true);
    try {
      const ids = Array.from(selectedScans);
      const results = await Promise.allSettled(
        ids.map(id => axiosClient.delete(`/hairstyles/admin/scan/${id}`))
      );

      const successIds = [];
      const failedIds = [];
      results.forEach((r, idx) => {
        const id = ids[idx];
        if (r.status === 'fulfilled') successIds.push(id);
        else failedIds.push(id);
      });

      if (successIds.length > 0) {
        setScans(prev => prev.filter(s => !successIds.includes(s.id)));
        setRemovedScansCount(prev => prev + successIds.length);
        toast.success(`Deleted ${successIds.length} scan(s)`);
      }

      if (failedIds.length > 0) {
        toast.error(`${failedIds.length} scan(s) failed to delete. Please try again.`);
      }

      // Keep failed selections so user can retry; otherwise clear
      setSelectedScans(new Set(failedIds));
    } catch (error) {
      console.error('Error deleting scans:', error);
      toast.error('Failed to delete selected scans');
    } finally {
      setBulkActionLoading(false);
    }
  };

  if (!hasPermission('ai-analysis')) {
    return (
      <div className="text-center py-12">
        <p className="text-zinc-600 dark:text-zinc-400">You don't have permission to access AI Analysis.</p>
      </div>
    );
  }

  return (
    <div className="relative min-h-screen">
      {/* Brand Glass Morphism Background Elements - Hidden in light mode */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none hidden dark:block">
        <div className="absolute top-10 left-10 w-96 h-96 bg-gradient-to-br from-[#7d6633]/25 to-[#5a4d26]/20 rounded-full blur-3xl"></div>
        <div className="absolute top-32 right-16 w-80 h-80 bg-gradient-to-br from-[#a0824a]/20 to-[#7d6633]/25 rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 left-1/3 w-72 h-72 bg-gradient-to-br from-[#d4b47a]/15 to-[#a0824a]/20 rounded-full blur-3xl"></div>
      </div>

      <div className="relative space-y-6 z-10">
        {/* Header */}
        <Card>
          <CardHeader
            title="AI Analysis"
            subtitle="Comprehensive analysis of all client AI-generated hairstyles"
            icon={
              <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ color: ACCENT }}>
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
            }
          />
        </Card>

        {/* Analytics Dashboard */}
        {(analytics || scans.length > 0 || ledgerCreditsUsed > 0) && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card>
              <div className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-zinc-600 dark:text-zinc-400">Total Scans</p>
                    <p className="text-2xl font-bold text-zinc-900 dark:text-zinc-100">{scans.length}</p>
                    <p className="text-xs text-zinc-500 dark:text-zinc-400 mt-0.5">Current view</p>
                  </div>
                  <div className="h-12 w-12 rounded-xl bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
                    <svg className="h-6 w-6 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                    </svg>
                  </div>
                </div>
              </div>
            </Card>

            <Card>
              <div className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-zinc-600 dark:text-zinc-400">Credits Used</p>
                    <p className="text-2xl font-bold text-zinc-900 dark:text-zinc-100">{ledgerCreditsUsed}</p>
                    <p className="text-xs text-zinc-500 dark:text-zinc-400 mt-0.5">From ledger (immutable)</p>
                  </div>
                  <div className="h-12 w-12 rounded-xl bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center">
                    <svg className="h-6 w-6 text-purple-600 dark:text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                    </svg>
                  </div>
                </div>
              </div>
            </Card>

            <Card>
              <div className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-zinc-600 dark:text-zinc-400">Removed Scans</p>
                    <p className="text-2xl font-bold text-zinc-900 dark:text-zinc-100">{removedScansCount}</p>
                    <p className="text-xs text-zinc-500 dark:text-zinc-400 mt-0.5">This session</p>
                  </div>
                  <div className="h-12 w-12 rounded-xl bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
                    <svg className="h-6 w-6 text-red-600 dark:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6M9 7h6m-7 0V5a2 2 0 012-2h4a2 2 0 012 2v2" />
                    </svg>
                  </div>
                </div>
              </div>
            </Card>
          </div>
        )}

        {/* Filters */}
        <Card>
          <div className="p-4">
            <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4">
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-1">Search by Client</label>
                <input
                  type="text"
                  placeholder="Client name..."
                  value={filters.clientSearch}
                  onChange={(e) => handleFilterChange('clientSearch', e.target.value)}
                  className="w-full px-3 py-2 rounded-lg border border-white/20 dark:border-white/10 bg-white/50 dark:bg-zinc-800/50 backdrop-blur-sm text-zinc-900 dark:text-zinc-100 focus:outline-none focus:ring-2 focus:ring-[--accent]/20 focus:border-[--accent]/30 transition-all text-sm"
                  style={{ '--accent': ACCENT }}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-1">Date From</label>
                <input
                  type="date"
                  value={filters.dateFrom}
                  onChange={(e) => handleFilterChange('dateFrom', e.target.value)}
                  className="w-full px-3 py-2 rounded-lg border border-white/20 dark:border-white/10 bg-white/50 dark:bg-zinc-800/50 backdrop-blur-sm text-zinc-900 dark:text-zinc-100 focus:outline-none focus:ring-2 focus:ring-[--accent]/20 focus:border-[--accent]/30 transition-all text-sm"
                  style={{ '--accent': ACCENT }}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-1">Date To</label>
                <input
                  type="date"
                  value={filters.dateTo}
                  onChange={(e) => handleFilterChange('dateTo', e.target.value)}
                  className="w-full px-3 py-2 rounded-lg border border-white/20 dark:border-white/10 bg-white/50 dark:bg-zinc-800/50 backdrop-blur-sm text-zinc-900 dark:text-zinc-100 focus:outline-none focus:ring-2 focus:ring-[--accent]/20 focus:border-[--accent]/30 transition-all text-sm"
                  style={{ '--accent': ACCENT }}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-1">Results</label>
                <select
                  value={filters.limit}
                  onChange={(e) => handleFilterChange('limit', parseInt(e.target.value))}
                  className="w-full px-3 py-2 rounded-lg border border-white/20 dark:border-white/10 bg-white/50 dark:bg-zinc-800/50 backdrop-blur-sm text-zinc-900 dark:text-zinc-100 focus:outline-none focus:ring-2 focus:ring-[--accent]/20 focus:border-[--accent]/30 transition-all text-sm"
                  style={{ '--accent': ACCENT }}
                >
                  <option value={10}>10</option>
                  <option value={20}>20</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </select>
              </div>

              <div className="flex items-end">
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => setFilters({
                    clientSearch: '',
                    dateFrom: '',
                    dateTo: '',
                    limit: 20,
                    offset: 0
                  })}
                  className="w-full px-4 py-2 rounded-lg border border-white/20 dark:border-white/10 bg-white/50 dark:bg-zinc-800/50 backdrop-blur-sm text-zinc-700 dark:text-zinc-300 hover:bg-white/70 dark:hover:bg-zinc-800/70 transition-all text-sm"
                >
                  Clear Filters
                </motion.button>
              </div>
            </div>
          </div>
        </Card>

        {/* Scans List */}
        <Card>
          <CardHeader
            title="Client Hairstyle Scans"
            subtitle={`${scans.length} scans found`}
            icon={
              <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ color: ACCENT }}>
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
            }
            action={
              <div className="flex items-center gap-3">
                <label
                  className="flex items-center gap-2 text-sm text-zinc-700 dark:text-zinc-300 select-none"
                  onClick={(e) => e.stopPropagation()}
                >
                  <input
                    ref={selectAllRef}
                    type="checkbox"
                    checked={scans.length > 0 && selectedScans.size === scans.length}
                    onChange={handleSelectAll}
                    className="h-4 w-4 rounded border-zinc-300 dark:border-white/20 bg-white/70 dark:bg-zinc-800/60 text-[--accent] focus:ring-[--accent]/30"
                    style={{ '--accent': ACCENT }}
                    disabled={scans.length === 0}
                  />
                  <span className="hidden sm:inline">Select All</span>
                  <span className="sm:hidden">All</span>
                </label>

                {selectedScans.size > 0 && (
                  <motion.button
                    whileHover={{ scale: 1.03 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={handleBulkDelete}
                    disabled={bulkActionLoading}
                    className="px-3 py-2 rounded-xl bg-red-600 hover:bg-red-700 text-white text-sm font-medium transition-all shadow-lg disabled:opacity-50"
                    title="Delete selected scans"
                  >
                    {bulkActionLoading ? 'Deleting…' : `Delete (${selectedScans.size})`}
                  </motion.button>
                )}
              </div>
            }
          />
          
          <div className="p-4">
            {loading ? (
              <div className="text-center py-12">
                <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-[--accent]" style={{ '--accent': ACCENT }}></div>
                <p className="text-zinc-600 dark:text-zinc-400 mt-4">Loading hairstyle analysis data...</p>
              </div>
            ) : scans.length === 0 ? (
              <div className="text-center py-12">
                <svg className="h-16 w-16 text-zinc-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
                <p className="text-zinc-600 dark:text-zinc-400">No hairstyle scans found matching your criteria</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
                {scans.map((scan) => (
                  <motion.div
                    key={scan.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="group relative overflow-hidden rounded-2xl border border-white/20 dark:border-white/10 bg-white/40 dark:bg-zinc-800/40 backdrop-blur-xl hover:bg-white/60 dark:hover:bg-zinc-800/60 transition-all duration-300 hover:shadow-xl hover:shadow-black/5 cursor-pointer"
                    onClick={() => openImageModal({ path: scan.originalImage }, 'original', scan)}
                  >
                    {/* Header with Client Info */}
                    <div className="p-4 border-b border-white/10 dark:border-white/10">
                      <div className="flex items-center gap-3">
                        <div className="h-10 w-10 rounded-full bg-gradient-to-r from-blue-500 to-purple-600 flex items-center justify-center shadow-lg">
                          <svg className="h-5 w-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                          </svg>
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className="font-semibold text-zinc-900 dark:text-zinc-100 truncate">
                            {scan.client?.name || (scan.client?.userId ? `Client #${scan.client.userId}` : 'Unknown Client')}
                          </h3>
                          <div className="flex items-center gap-2 mt-1">
                            <span className="text-xs text-zinc-500 dark:text-zinc-400">Scan #{scan.id}</span>
                            <span className="text-xs text-zinc-400">•</span>
                            <span className="text-xs text-zinc-500 dark:text-zinc-400">
                              {new Date(scan.createdAt).toLocaleDateString()}
                            </span>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 text-xs text-zinc-500 dark:text-zinc-400">
                          <svg className="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                          </svg>
                          <span>{scan.creditsUsed || 0} credits</span>
                        </div>
                        {/* Selection: inline action so it never blocks avatar/credits */}
                        <div onClick={(e) => e.stopPropagation()}>
                          <button
                            type="button"
                            onClick={() => handleSelectScan(scan.id)}
                            className="h-8 w-8 rounded-full bg-white/70 dark:bg-zinc-800/70 border border-white/30 dark:border-white/10 backdrop-blur flex items-center justify-center shadow-sm hover:bg-white/90 dark:hover:bg-zinc-800/90 transition-all"
                            aria-label={selectedScans.has(scan.id) ? 'Deselect scan' : 'Select scan'}
                            title={selectedScans.has(scan.id) ? 'Deselect scan' : 'Select scan'}
                          >
                            <input
                              type="checkbox"
                              checked={selectedScans.has(scan.id)}
                              readOnly
                              className="h-4 w-4 rounded border-zinc-300 dark:border-white/20 bg-white dark:bg-zinc-800 text-[--accent] focus:ring-[--accent]/30"
                              style={{ '--accent': ACCENT }}
                              tabIndex={-1}
                            />
                          </button>
                        </div>
                      </div>
                    </div>

                    {/* Images Section */}
                    <AdminAIThumbnailGallery
                      originalImage={scan.originalImage}
                      generatedImages={scan.generatedImages || []}
                      onOriginalClick={() => openImageModal({ path: scan.originalImage }, 'original', scan)}
                      onGeneratedClick={(image) => openImageModal(image, 'generated', scan)}
                    />

                    {/* Footer with Actions */}
                    <div className="p-4 border-t border-white/10 dark:border-white/10 bg-white/20 dark:bg-zinc-800/20">
                      <div className="flex items-center justify-end">
                        <div className="flex gap-2">
                          <motion.button
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            onClick={(e) => { e.stopPropagation(); window.open('/admin/clients', '_blank'); }}
                            className="px-3 py-1.5 text-xs font-medium rounded-lg bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 hover:bg-blue-200 dark:hover:bg-blue-900/50 transition-all"
                          >
                            View Client
                          </motion.button>
                          <motion.button
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            onClick={(e) => { e.stopPropagation(); handleDeleteScan(scan.id); }}
                            disabled={!!actionLoading[scan.id]}
                            className="px-3 py-1.5 text-xs font-medium rounded-lg bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 hover:bg-red-200 dark:hover:bg-red-900/50 transition-all disabled:opacity-50"
                          >
                            {actionLoading[scan.id] ? 'Deleting…' : 'Delete'}
                          </motion.button>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            )}
          </div>
        </Card>

        <AdminAIComparisonModal
          isOpen={showModal}
          onClose={closeModal}
          activeScan={activeScan}
          selectedImage={selectedImage}
          showAnalysisInformation={false}
          showProvider={false}
          showStatus={false}
        />

        {/* Confirmation Modal (delete / bulk delete) */}
        {showConfirmModal && confirmData && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="p-6 rounded-2xl shadow-2xl max-w-md w-full mx-4 bg-white/90 dark:bg-zinc-900/90 backdrop-blur-xl border border-white/20 dark:border-white/10"
            >
              <div className="flex items-center mb-4">
                <div className="h-10 w-10 rounded-xl bg-orange-100 dark:bg-orange-900/30 flex items-center justify-center mr-3">
                  <svg className="h-5 w-5 text-orange-600 dark:text-orange-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                  </svg>
                </div>
                <h3 className="text-lg font-medium text-zinc-900 dark:text-zinc-100">
                  {confirmData.title}
                </h3>
              </div>

              <div className="mb-6">
                <p className="text-sm text-zinc-600 dark:text-zinc-400 mb-3">
                  {confirmData.message}
                </p>
                {confirmData.details && (
                  <div className="bg-zinc-100 dark:bg-zinc-800/50 p-4 rounded-xl text-sm border border-white/10 dark:border-white/10">
                    {confirmData.details.map((detail, index) => (
                      <div key={index} className="text-zinc-700 dark:text-zinc-300">{detail}</div>
                    ))}
                  </div>
                )}
                {confirmData.warning && (
                  <p className="text-sm text-red-600 dark:text-red-400 mt-3 font-medium">
                    {confirmData.warning}
                  </p>
                )}
              </div>

              <div className="flex justify-end gap-3">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => setShowConfirmModal(false)}
                  className="px-4 py-2 rounded-xl border border-white/20 dark:border-white/10 bg-white/50 dark:bg-zinc-800/50 backdrop-blur-sm text-zinc-700 dark:text-zinc-300 hover:bg-white/70 dark:hover:bg-zinc-800/70 transition-all"
                  disabled={bulkActionLoading}
                >
                  Cancel
                </motion.button>
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => {
                    setShowConfirmModal(false);
                    confirmData.onConfirm();
                  }}
                  className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-xl transition-all shadow-lg"
                  disabled={bulkActionLoading}
                >
                  Confirm
                </motion.button>
              </div>
            </motion.div>
          </div>
        )}
      </div>
    </div>
  );
};

export default EnhancedAIHairstyleAnalysis;
